import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Portal } from './Portal';
import { useT } from '../i18n';

interface Replier {
  contactId: string;
  name: string;
  phone: string;
  sentAt: string;
  firstReplyAt: string;
  minutesToReply: number;
  likelyAutomatic: boolean;
  firstReplyText: string | null;
  firstReplyType: string | null;
  replyCount: number;
  conversationId: string | null;
  unsubscribed: boolean;
  receiptStatus: string | null;
}

interface Analytics {
  campaign: { id: string; name: string; status: string };
  windowDays: number;
  totals: { total: number; pending: number; sent: number; failed: number; skipped: number };
  funnel: {
    sent: number; delivered: number; read: number; replied: number;
    humanReplies: number; autoReplies: number; unsubscribed: number; receiptKnown: number;
  };
  rates: {
    deliveryRate: number; readRate: number; replyRate: number;
    replyRateOfDelivered: number; unsubscribeRate: number; failureRate: number;
  };
  replyTiming: { medianMinutes: number | null; buckets: { under5m: number; under1h: number; under24h: number; over24h: number } };
  repliesByDay: Array<{ date: string; count: number }>;
  failures: Array<{ reason: string; count: number }>;
  variations: Array<{ variation: string; sent: number; replied: number; replyRate: number }>;
  repliers: Replier[];
  repliersTruncated: boolean;
}

const WINDOW_OPTIONS = [1, 3, 7, 14, 30];

// Os motivos de falha vêm do servidor em inglês; traduzimos os conhecidos ao mostrar
const FAILURE_KEYS: Record<string, string> = {
  'Number is not on WhatsApp': 'fail.notOnWhatsApp',
  'Media file could not be downloaded': 'fail.media',
  'Provider rejected the credentials': 'fail.credentials',
  'Connection timeout': 'fail.timeout',
  'Contact unsubscribed': 'fail.unsubscribed',
  'Unknown error': 'fail.unknown',
};

function authHeaders(): HeadersInit {
  const token = localStorage.getItem('auth_token');
  return token ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } : { 'Content-Type': 'application/json' };
}

function formatDuration(minutes: number | null): string {
  if (minutes === null) return '—';
  if (minutes < 1) return `${Math.round(minutes * 60)}s`;
  if (minutes < 60) return `${Math.round(minutes)} min`;
  if (minutes < 1440) return `${(minutes / 60).toFixed(1)} h`;
  return `${(minutes / 1440).toFixed(1)} d`;
}

function formatNumber(value: number): string {
  return value.toLocaleString('en-US');
}

function KpiCard({ label, value, sub, tone }: { label: string; value: string; sub?: string; tone: string }) {
  return (
    <div className={`rounded-xl border p-4 ${tone}`}>
      <div className="text-xs font-medium uppercase tracking-wide opacity-70">{label}</div>
      <div className="text-2xl font-bold mt-1">{value}</div>
      {sub && <div className="text-xs mt-1 opacity-80">{sub}</div>}
    </div>
  );
}

function Bar({ label, value, max, suffix, color }: { label: string; value: number; max: number; suffix?: string; color: string }) {
  const width = max > 0 ? Math.max((value / max) * 100, value > 0 ? 2 : 0) : 0;
  return (
    <div className="mb-3">
      <div className="flex justify-between text-sm mb-1">
        <span className="text-gray-700">{label}</span>
        <span className="font-semibold text-gray-900">
          {formatNumber(value)}{suffix ? <span className="text-gray-500 font-normal"> {suffix}</span> : null}
        </span>
      </div>
      <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${width}%` }} />
      </div>
    </div>
  );
}

export function CampaignAnalyticsModal({ campaignId, onClose }: { campaignId: string; onClose: () => void }) {
  const t = useT();
  const [data, setData] = useState<Analytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [windowDays, setWindowDays] = useState(7);
  const [search, setSearch] = useState('');
  const [hideAutomatic, setHideAutomatic] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`/api/campaigns/${campaignId}/analytics?windowDays=${windowDays}`, { headers: authHeaders() });
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || `HTTP ${response.status}`);
      }
      setData(await response.json());
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t('an.errLoad'));
    } finally {
      setLoading(false);
    }
  }, [campaignId, windowDays]);

  useEffect(() => {
    load();
  }, [load]);

  const syncReceipts = async () => {
    setSyncing(true);
    try {
      const response = await fetch(`/api/campaigns/${campaignId}/analytics/sync-receipts`, { method: 'POST', headers: authHeaders() });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(result.error || `HTTP ${response.status}`);
      toast.success(result.note || t('an.synced', { count: result.updated ?? 0 }));
      await load();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t('an.errSync'));
    } finally {
      setSyncing(false);
    }
  };

  const repliers = useMemo(() => {
    if (!data) return [];
    const term = search.trim().toLowerCase();
    return data.repliers.filter(item => {
      if (hideAutomatic && item.likelyAutomatic) return false;
      if (!term) return true;
      return (
        item.name?.toLowerCase().includes(term) ||
        item.phone.includes(term) ||
        (item.firstReplyText || '').toLowerCase().includes(term)
      );
    });
  }, [data, search, hideAutomatic]);

  const exportCsv = () => {
    if (!data) return;
    const header = ['Name', 'Phone', 'Sent at', 'First reply at', 'Minutes to reply', 'Likely automatic', 'Replies', 'First reply', 'Unsubscribed'];
    const escape = (value: unknown) => `"${String(value ?? '').replace(/"/g, '""')}"`;
    const lines = repliers.map(item => [
      item.name, item.phone, new Date(item.sentAt).toISOString(), new Date(item.firstReplyAt).toISOString(),
      item.minutesToReply, item.likelyAutomatic ? 'yes' : 'no', item.replyCount,
      item.firstReplyText || (item.firstReplyType ? `[${item.firstReplyType}]` : ''), item.unsubscribed ? 'yes' : 'no',
    ].map(escape).join(','));
    // BOM para o Excel abrir o árabe corretamente
    const blob = new Blob(['﻿' + [header.map(escape).join(','), ...lines].join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${data.campaign.name}-replies.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const receiptsMissing = data && data.funnel.sent > 0 && data.funnel.receiptKnown < data.funnel.sent;
  const bucketMax = data ? Math.max(...Object.values(data.replyTiming.buckets), 1) : 1;
  const failureMax = data ? Math.max(...data.failures.map(f => f.count), 1) : 1;

  return (
    <Portal>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 backdrop-blur-sm" style={{ zIndex: 9999 }}>
        <div className="bg-white rounded-xl shadow-xl max-w-6xl w-full max-h-[95vh] overflow-y-auto">
          {/* Cabeçalho */}
          <div className="sticky top-0 z-10 flex flex-wrap gap-3 justify-between items-center px-6 py-4 border-b bg-gradient-to-r from-emerald-50 to-blue-50">
            <div>
              <h3 className="text-xl font-semibold text-gray-900">{t('an.title')}</h3>
              <p className="text-sm text-gray-600" dir="auto">{data?.campaign.name || '…'}</p>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-xs text-gray-600">{t('an.window')}</label>
              <select
                value={windowDays}
                onChange={event => setWindowDays(Number(event.target.value))}
                className="text-sm border border-gray-300 rounded-md px-2 py-1 bg-white"
              >
                {WINDOW_OPTIONS.map(option => (
                  <option key={option} value={option}>{option === 1 ? t('an.day') : t('an.days', { count: option })}</option>
                ))}
              </select>
              <button
                onClick={syncReceipts}
                disabled={syncing}
                className="text-sm px-3 py-1.5 rounded-md bg-white border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
                title={t('an.syncTitle')}
              >
                {syncing ? t('an.syncing') : `↻ ${t('an.sync')}`}
              </button>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-2 rounded-full hover:bg-white" title={t('conn.close')}>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {loading && !data && <div className="p-12 text-center text-gray-400">{t('an.loading')}</div>}

          {data && (
            <div className={`p-6 space-y-6 ${loading ? 'opacity-60' : ''}`}>
              {receiptsMissing && (
                <div className="rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-800 flex items-center justify-between gap-3">
                  <span>{t('an.syncMissing', { count: formatNumber(data.funnel.sent - data.funnel.receiptKnown) })}</span>
                  <button onClick={syncReceipts} disabled={syncing} className="shrink-0 px-3 py-1 rounded-md bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50">
                    {syncing ? t('an.syncing') : t('an.syncNow')}
                  </button>
                </div>
              )}

              {/* Indicadores principais */}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                <KpiCard label={t('an.kpiSent')} value={formatNumber(data.funnel.sent)} sub={t('an.kpiPending', { count: formatNumber(data.totals.pending) })} tone="bg-slate-50 border-slate-200 text-slate-900" />
                <KpiCard label={t('an.kpiDelivered')} value={`${data.rates.deliveryRate}%`} sub={t('an.kpiDeliveredSub', { sent: formatNumber(data.funnel.delivered), total: formatNumber(data.funnel.sent) })} tone="bg-blue-50 border-blue-200 text-blue-900" />
                <KpiCard label={t('an.kpiRead')} value={`${data.rates.readRate}%`} sub={t('an.kpiReadSub', { count: formatNumber(data.funnel.read) })} tone="bg-indigo-50 border-indigo-200 text-indigo-900" />
                <KpiCard label={t('an.kpiReplied')} value={`${data.rates.replyRate}%`} sub={t('an.kpiRepliedSub', { people: formatNumber(data.funnel.replied), human: formatNumber(data.funnel.humanReplies) })} tone="bg-emerald-50 border-emerald-200 text-emerald-900" />
                <KpiCard label={t('an.kpiUnsub')} value={formatNumber(data.funnel.unsubscribed)} sub={t('an.kpiUnsubSub', { rate: data.rates.unsubscribeRate })} tone="bg-rose-50 border-rose-200 text-rose-900" />
                <KpiCard label={t('an.kpiFailed')} value={formatNumber(data.totals.failed)} sub={t('an.kpiFailedSub', { rate: data.rates.failureRate })} tone="bg-orange-50 border-orange-200 text-orange-900" />
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Funil */}
                <div className="rounded-xl border border-gray-200 p-5">
                  <h4 className="font-semibold text-gray-900 mb-4">{t('an.funnel')}</h4>
                  <Bar label={t('an.kpiSent')} value={data.funnel.sent} max={data.funnel.sent} color="bg-slate-400" />
                  <Bar label={t('an.kpiDelivered')} value={data.funnel.delivered} max={data.funnel.sent} suffix={`(${data.rates.deliveryRate}%)`} color="bg-blue-500" />
                  <Bar label={t('an.kpiRead')} value={data.funnel.read} max={data.funnel.sent} color="bg-indigo-500" />
                  <Bar label={t('an.kpiReplied')} value={data.funnel.replied} max={data.funnel.sent} suffix={`(${data.rates.replyRate}%)`} color="bg-emerald-500" />
                  <Bar label={t('an.funnelHuman')} value={data.funnel.humanReplies} max={data.funnel.sent} color="bg-emerald-300" />
                </div>

                {/* Tempo de resposta */}
                <div className="rounded-xl border border-gray-200 p-5">
                  <h4 className="font-semibold text-gray-900">{t('an.timing')}</h4>
                  <p className="text-sm text-gray-500 mb-4">{t('an.median')} <span className="font-semibold text-gray-800">{formatDuration(data.replyTiming.medianMinutes)}</span></p>
                  <Bar label={t('an.under5m')} value={data.replyTiming.buckets.under5m} max={bucketMax} color="bg-emerald-500" />
                  <Bar label={t('an.under1h')} value={data.replyTiming.buckets.under1h} max={bucketMax} color="bg-emerald-400" />
                  <Bar label={t('an.under24h')} value={data.replyTiming.buckets.under24h} max={bucketMax} color="bg-emerald-300" />
                  <Bar label={t('an.over24h')} value={data.replyTiming.buckets.over24h} max={bucketMax} color="bg-emerald-200" />
                  {data.funnel.autoReplies > 0 && (
                    <p className="text-xs text-gray-500 mt-3">
                      {t('an.autoNote', { count: data.funnel.autoReplies })}
                    </p>
                  )}
                </div>

                {/* Falhas */}
                <div className="rounded-xl border border-gray-200 p-5">
                  <h4 className="font-semibold text-gray-900 mb-4">{t('an.failures')}</h4>
                  {data.failures.length === 0 && <p className="text-sm text-gray-400">{t('an.noFailures')}</p>}
                  {data.failures.map(failure => (
                    <Bar
                      key={failure.reason}
                      label={FAILURE_KEYS[failure.reason] ? t(FAILURE_KEYS[failure.reason]) : failure.reason}
                      value={failure.count}
                      max={failureMax}
                      color="bg-orange-400"
                    />
                  ))}
                </div>
              </div>

              {/* Respostas por dia */}
              {data.repliesByDay.length > 1 && (
                <div className="rounded-xl border border-gray-200 p-5">
                  <h4 className="font-semibold text-gray-900 mb-4">{t('an.perDay')}</h4>
                  <div className="flex items-end gap-2 h-32">
                    {data.repliesByDay.map(day => {
                      const max = Math.max(...data.repliesByDay.map(d => d.count), 1);
                      return (
                        <div key={day.date} className="flex-1 flex flex-col items-center gap-1 min-w-0">
                          <span className="text-xs font-semibold text-gray-700">{day.count}</span>
                          <div className="w-full bg-emerald-400 rounded-t" style={{ height: `${(day.count / max) * 90}px` }} />
                          <span className="text-[10px] text-gray-500 truncate">{day.date.slice(5)}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Variações */}
              {data.variations.length > 1 && (
                <div className="rounded-xl border border-gray-200 p-5">
                  <h4 className="font-semibold text-gray-900 mb-3">{t('an.variations')}</h4>
                  <table className="w-full text-sm">
                    <thead><tr className="text-left text-gray-500"><th className="text-start py-1">{t('an.variation')}</th><th>{t('an.kpiSent')}</th><th>{t('an.kpiReplied')}</th><th>{t('an.rate')}</th></tr></thead>
                    <tbody>
                      {data.variations.map(v => (
                        <tr key={v.variation} className="border-t">
                          <td className="py-2" dir="auto">{v.variation}</td><td>{v.sent}</td><td>{v.replied}</td><td className="font-semibold">{v.replyRate}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Quem respondeu */}
              <div className="rounded-xl border border-gray-200">
                <div className="flex flex-wrap gap-3 items-center justify-between p-4 border-b">
                  <h4 className="font-semibold text-gray-900">
                    {t('an.whoReplied')} <span className="text-gray-400 font-normal">({repliers.length})</span>
                  </h4>
                  <div className="flex items-center gap-3">
                    <label className="flex items-center gap-1.5 text-sm text-gray-600">
                      <input type="checkbox" checked={hideAutomatic} onChange={event => setHideAutomatic(event.target.checked)} />
                      {t('an.hideAuto')}
                    </label>
                    <input
                      value={search}
                      onChange={event => setSearch(event.target.value)}
                      placeholder={t('an.searchReplies')}
                      className="text-sm border border-gray-300 rounded-md px-3 py-1.5 w-56"
                    />
                    <button onClick={exportCsv} disabled={repliers.length === 0} className="text-sm px-3 py-1.5 rounded-md bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50">
                      {t('an.exportCsv')}
                    </button>
                  </div>
                </div>

                {repliers.length === 0 ? (
                  <p className="p-8 text-center text-sm text-gray-400">{t('an.noReplies')}</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 text-start text-gray-500">
                        <tr>
                          <th className="text-start px-4 py-2">{t('an.colContact')}</th>
                          <th className="text-start px-4 py-2">{t('an.colAfter')}</th>
                          <th className="text-start px-4 py-2">{t('an.colFirst')}</th>
                          <th className="px-4 py-2 text-center">{t('an.colReplies')}</th>
                          <th className="text-start px-4 py-2"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {repliers.map(item => (
                          <tr key={`${item.contactId}-${item.sentAt}`} className="border-t hover:bg-gray-50">
                            <td className="px-4 py-2">
                              <div className="font-medium text-gray-900" dir="auto">{item.name}</div>
                              <div className="text-xs text-gray-500">{item.phone}</div>
                            </td>
                            <td className="px-4 py-2 whitespace-nowrap">
                              {formatDuration(item.minutesToReply)}
                              {item.likelyAutomatic && (
                                <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-gray-200 text-gray-600">{t('an.auto')}</span>
                              )}
                            </td>
                            <td className="px-4 py-2 max-w-md">
                              <div className="truncate text-gray-700" dir="auto" title={item.firstReplyText || ''}>
                                {item.firstReplyText || <span className="italic text-gray-400">[{item.firstReplyType || 'message'}]</span>}
                              </div>
                              {item.unsubscribed && <span className="text-[10px] px-1.5 py-0.5 rounded bg-rose-100 text-rose-700">{t('inbox.unsubscribed')}</span>}
                            </td>
                            <td className="px-4 py-2 text-center">{item.replyCount}</td>
                            <td className="px-4 py-2 text-right">
                              {item.conversationId && (
                                <Link to={`/inbox?c=${item.conversationId}`} className="text-emerald-700 hover:text-emerald-900 font-medium whitespace-nowrap">
                                  {t('an.openChat')}
                                </Link>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <p className="text-xs text-gray-400 leading-relaxed">
                {t('an.footnote')}
              </p>
            </div>
          )}
        </div>
      </div>
    </Portal>
  );
}
